import React, { useState, useMemo } from 'react'
import Grid from '../../../../components/Grid/Grid'
import GridItemVehicle from '../../../../components/Grid/Items/GridItemVehicle'
import * as portals from 'react-reverse-portal'
import TextInput from '../../../../components/FormElements/TextInput'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { Vehicle } from '../../../../lib/types/Vehicle'
import { Button, DropdownButton, RadioInput, Table } from '@demandlocal/components'
import SortingController from '../../../../components/Sorting/SortingController'
import { useGetMarketedInventory } from '../../../../api/dealership/inventory/useGetMarketedInventory'
import { useVehiclesListView } from '../../../../store/zustand/vehiclesListStore'
import { OutPortal } from 'react-reverse-portal'
import { motion } from 'framer-motion'
import { ReactComponent as IconChangeViewList } from '../../../../images/icons/icon_change_view_list.svg'
import { ReactComponent as IconChangeViewCard } from '../../../../images/icons/icon_change_view_card.svg'
import GoogleSheetsExport from '../../../../forms/Exports/GoogleSheetsExport'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { createColumnHelper } from '@tanstack/react-table'
import { formatCurrency, formatNumber } from '../../../../lib/utils/formatNumber'
import { ADMIN_VENDOR_MAP, VENDOR } from '../../../../lib/utils/vendors'
import { ReactComponent as GoogleIcon } from '../../../../images/icons/audience_google.svg'
import { ReactComponent as FacebookIcon } from '../../../../images/icons/audience_facebook.svg'
import { ReactComponent as TradeDeskIcon } from '../../../../images/icons/audience_tradedesk.svg'
import { ReactComponent as BingIcon } from '../../../../images/icons/audience_bing.svg'
import GridItemMarketedVehicle from '../../../../components/Grid/Items/GridItemMarketedVehicle'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ReactComponent as AddInventoryImage } from '../../../../images/add_inventory.svg'
import { isEmpty } from 'lodash'
import LoadingAnimation from '../../../../components/LoadingAnimation'

const columnHelper = createColumnHelper<Vehicle>()

const VehiclesList = (): JSX.Element => {
  const dealership = useDealership()
  const [filters, setFilters] = useState<Partial<Vehicle>>({})
  const { view, setView } = useVehiclesListView()
  const [sorting, setSorting] = useState<Record<string, unknown>>({ clicks: 'desc' })
  const filtersPortalNode = useMemo(() => portals.createHtmlPortalNode(), [])
  const sortingPortalNode = useMemo(() => portals.createHtmlPortalNode(), [])
  const [filteredTotal, setFilteredTotal] = useState<number | undefined>(undefined)
  const [exportGoogleSheet, setExportGoogleSheet] = useState<boolean>(false)
  const { t } = useTranslation('routes')
  const { data, isLoading } = useGetMarketedInventory({ pageIndex: 1, pageSize: 1 })

  const displayFilterCount = useMemo(() => {
    return Boolean(
      filteredTotal &&
        filteredTotal > 0 &&
        Object.values(filters).filter((f) => !!f)?.length > 0,
    )
  }, [filteredTotal, filters])

  const inventoryColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'vehicle',
        header: 'Vehicle',
        cell: ({
          row: {
            original: { year, make, model, vin_number },
          },
        }) => (
          <Link
            className="text-primary-persian-blue font-bold text-xs cursor-pointer"
            to={t('dealership.vehicles.vehicle', {
              dealership,
              vehicle: {
                vin: vin_number,
              },
            })}>
            {year} {make} {model}
          </Link>
        ),
      }),
      columnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor('stock_number', {
        header: 'Stock Number',
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => formatCurrency(Number(info.getValue())),
      }),
      columnHelper.accessor('msrp', {
        header: 'MSRP',
        cell: (info) => formatCurrency(Number(info.getValue())),
      }),
      columnHelper.accessor('mileage', {
        header: 'Mileage',
        cell: (info) => formatNumber(Number(info.getValue())),
      }),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => formatNumber(Number(info.getValue())),
      }),
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: (info) => formatNumber(Number(info.getValue())),
      }),
      columnHelper.display({
        id: 'vendor',
        header: 'Marketed on',
        cell: ({ row }) => {
          return (
            <div className="flex flex-row gap-2 items-center">
              {Array.from(
                new Set(
                  row.original.vendor_id
                    ?.split(',')
                    ?.map((id) => ([21, 26, 29].includes(Number(id)) ? 21 : id)), // Aggregate TTD vendors
                ),
              )?.map((id) => {
                return (
                  <div key={id}>
                    {ADMIN_VENDOR_MAP[Number(id)] === VENDOR.Google && <GoogleIcon />}
                    {ADMIN_VENDOR_MAP[Number(id)] === VENDOR.Facebook && <FacebookIcon />}
                    {ADMIN_VENDOR_MAP[Number(id)] === VENDOR.TradeDesk && (
                      <TradeDeskIcon />
                    )}
                    {[3].includes(Number(id)) && <BingIcon />}
                  </div>
                )
              })}
            </div>
          )
        },
      }),
    ],
    [dealership, t],
  )

  if (isLoading) {
    return (
      <div className="my-auto mx-auto ">
        <LoadingAnimation title="Loading..." />
      </div>
    )
  }

  if (Number(data?.pagination.total) === 0) {
    return (
      <div
        className={
          'flex flex-col text-center my-auto mx-auto w-2/3 items-center justify-center px-10'
        }>
        <AddInventoryImage className="mx-auto" />
        <span className={'font-bold text-3xl font-Spartan pb-1 pt-8'}>
          You are not marketing your inventory{' '}
        </span>
        <span className={'text-base font-Spartan pt-1 pb-8'}>
          Your inventory is invisible. Every car you are not marketing is an opportunity
          for someone else. Start reaching the right buyers today!
        </span>
        <Link to={t('dealership.campaigns.create', { dealership })}>
          <Button>Market your vehicles</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className={'flex flex-col flex-1 pb-8'}>
      <div className="text-md rounded-xl p-2 bg-gainsboro-gray/40 border-2 mb-4">
        Explore vehicles with active impressions served, highlighting your most promoted
        inventory.
      </div>
      <div className={'pb-4 flex flex-row justify-between'}>
        <div className={'flex flex-1 flex-row justify-between gap-4'}>
          <OutPortal node={sortingPortalNode} />
          <div className="flex flex-row gap-1 items-center">
            {displayFilterCount && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                key={'filters-total'}
                className={
                  'h-5 bg-primary-persian-blue text-center flex items-center text-white p-1.5 rounded-md text-xs font-bold'
                }>
                <span>{filteredTotal} Vehicles</span>
              </motion.div>
            )}
            <div>
              <OutPortal node={filtersPortalNode} />
            </div>
            <DropdownButton
              onClick={(e) => setExportGoogleSheet(false)}
              options={[
                {
                  label: 'Google sheet',
                  onClick: (e) => setExportGoogleSheet(true),
                },
              ]}>
              Export
            </DropdownButton>
            <div className={'flex items-center gap-3'}>
              {view === 'grid' && (
                <div onClick={() => setView(view === 'grid' ? 'table' : 'grid')}>
                  <IconChangeViewList
                    className={
                      'cursor-pointer stroke-primary-xiketic fill-primary-xiketic'
                    }
                  />
                </div>
              )}
              {view === 'table' && (
                <div onClick={() => setView(view === 'table' ? 'grid' : 'table')}>
                  <IconChangeViewCard
                    className={'cursor-pointer stroke-primary-xiketic'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {exportGoogleSheet && (
        <GoogleSheetsExport
          setExportGoogleSheet={setExportGoogleSheet}
          apiEndpoint={`/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/inventory/marketed`}
          filters={filters}
          columns={inventoryColumns}
        />
      )}
      {view === 'grid' && (
        <SortingController
          sorting={sorting}
          portalNode={sortingPortalNode}
          setSorting={setSorting}
          choices={[
            { label: 'Price', key: 'price' },
            { label: 'Year', key: 'year' },
            { label: 'Impressions', key: 'impressions' },
            { label: 'Clicks', key: 'clicks' },
          ]}
        />
      )}
      <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
        <TextInput name={'vin_number'} placeholder={'VIN'} label={'VIN'} />
        <TextInput name={'raw_make'} placeholder={'Make'} label={'Make'} />
        <TextInput name={'raw_model'} placeholder={'Model'} label={'Model'} />
        <TextInput name={'stock_number'} placeholder={'Stock'} label={'Stock number'} />
        <TextInput name={'year'} placeholder={'Year'} label={'Year'} />
        <RadioInput
          name={'vehicle_state'}
          options={[
            { label: 'New', value: 'New' },
            { label: 'Used', value: 'Used' },
          ]}
          label={'Vehicle State'}
        />
      </FiltersContext>
      {view === 'grid' && (
        <Grid
          fetchFn={useGetMarketedInventory}
          gridComponent={GridItemMarketedVehicle}
          filters={filters}
          sorting={sorting as Record<string, string | undefined>}
          setTotalRows={(v) => setFilteredTotal(v)}
        />
      )}
      {view === 'table' && (
        <Table
          isDownloadable
          emptyLabel={'No vehicles found'}
          columns={inventoryColumns}
          filters={{ ...filters }}
          fetchFn={useGetMarketedInventory}
          setTotalRows={(v) => setFilteredTotal(v)}
          defaultSorting={[{ id: 'clicks', desc: true }]}
        />
      )}
    </div>
  )
}

export default VehiclesList
